"use client";

import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@utils/utils";
import styles from "./Accordion.module.scss";

interface AccordionProps {
  type?: "single" | "multiple";
  defaultValue?: string | string[];
  className?: string;
  children: React.ReactNode;
}

interface AccordionContextProps {
  expanded: Record<string, boolean>;
  toggleItem: (value: string) => void;
  type: "single" | "multiple";
  isClient: boolean;
}

const AccordionContext = React.createContext<AccordionContextProps | undefined>(
  undefined
);

export function Accordion({
  type = "single",
  defaultValue = [],
  className,
  children,
  ...props
}: AccordionProps) {
  // Initialize state based on defaultValue
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    if (Array.isArray(defaultValue) && defaultValue.length > 0) {
      return defaultValue.reduce((acc, value) => ({ ...acc, [value]: true }), {});
    } else if (typeof defaultValue === "string" && defaultValue) {
      return { [defaultValue]: true };
    }
    return {};
  });
  
  const [isClient, setIsClient] = useState(false);

  // Only run once on mount to set client-side flag
  useEffect(() => {
    setIsClient(true);
  }, []);

  const toggleItem = (value: string) => {
    setExpanded((prev) => {
      if (type === "single") {
        return { [value]: !prev[value] };
      }
      return { ...prev, [value]: !prev[value] };
    });
  };

  return (
    <AccordionContext.Provider value={{ expanded, toggleItem, type, isClient }}>
      <div className={cn(styles.accordionRoot, className)} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

interface AccordionItemProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

export function AccordionItem({
  value,
  className,
  children,
  ...props
}: AccordionItemProps) {
  return (
    <div className={cn(styles.accordionItem, className)} {...props}>
      {children}
    </div>
  );
}

interface AccordionTriggerProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

export function AccordionTrigger({
  value,
  className,
  children,
  ...props
}: AccordionTriggerProps) {
  const context = React.useContext(AccordionContext);
  if (!context) {
    throw new Error("AccordionTrigger must be used within an Accordion");
  }

  const { expanded, toggleItem } = context;
  const isExpanded = expanded[value];

  return (
    <button
      type='button'
      className={cn(styles.accordionTrigger, className)}
      onClick={() => toggleItem(value)}
      aria-expanded={isExpanded}
      aria-controls={`accordion-content-${value}`}
      {...props}
    >
      {children}
      <ChevronDown
        className={styles.accordionIcon}
        aria-hidden='true'
      />
    </button>
  );
}

interface AccordionContentProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

export function AccordionContent({
  value,
  className,
  children,
  ...props
}: AccordionContentProps) {
  const context = React.useContext(AccordionContext);
  if (!context) {
    throw new Error("AccordionContent must be used within an Accordion");
  }

  const { expanded, isClient } = context;
  const isExpanded = expanded[value];

  // For server-side rendering, always show content
  // For client-side, respect the expanded state
  const shouldShow = !isClient || isExpanded;

  return (
    <div
      id={`accordion-content-${value}`}
      className={cn(
        styles.accordionContent,
        isClient && styles.transitionEnabled,
        shouldShow ? styles.expanded : styles.collapsed,
        className
      )}
      aria-hidden={!shouldShow}
      {...props}
    >
      <div className={cn(
        styles.accordionContentInner,
        shouldShow ? styles.visible : styles.invisible
        )}>
        {children}
      </div>
    </div>
  );
}
